import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEntityForm } from '@/shared/lib/useEntityForm';
import { useRouteId } from '@/shared/lib/useRouteId';
import { DetailLayout } from '@/shared/ui/DetailLayout';
import { FileUploadField } from '@/shared/ui/FileUploadField';
import { FilterSelect } from '@/shared/ui/FilterSelect';
import { FormField } from '@/shared/ui/FormField';
import { MultiSelect } from '@/shared/ui/MultiSelect';
import { SaveBar } from '@/shared/ui/SaveBar';
import { TextField } from '@/shared/ui/TextField';
import {
  CASHBACK_TYPE,
  EMPTY_PRIVILEGE,
  PRIVILEGE_TYPES,
  deletePrivilegeImage,
  fetchLevelOptions,
  fetchPrivilege,
  privilegeToValues,
  savePrivilege,
  uploadPrivilegeImage,
  type PrivilegeValues,
} from './api';

const SELECT_WIDTH = 300;

const PrivilegeForm = ({ id, initial }: { id?: number; initial: PrivilegeValues }) => {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const levels = useQuery({ queryKey: ['loyalty-level-options'], queryFn: fetchLevelOptions });

  const form = useEntityForm(
    initial,
    (values) => savePrivilege(id, values),
    (res) => {
      void qc.invalidateQueries({ queryKey: ['loyalty-privileges'] });
      if (!id && res.data?.id) navigate(`/loyalty/privileges/edit/${res.data.id}`, { replace: true });
    },
  );
  const { values, errors, setField } = form;

  return (
    <>
      <TextField
        label="Заголовок привилегии *"
        placeholder="Имя"
        value={values.name}
        errors={errors.name}
        onChange={(v) => setField('name', v)}
      />
      <TextField
        label="Текст первого абзаца *"
        placeholder="Введите текст"
        multiline
        rows={3}
        value={values.description}
        errors={errors.description}
        onChange={(v) => setField('description', v)}
      />
      <FormField label="Тип привилегии" errors={errors.type_id}>
        <FilterSelect
          options={PRIVILEGE_TYPES}
          value={values.typeId}
          isClearable={false}
          width={SELECT_WIDTH}
          onChange={(v) => setField('typeId', v, 'type_id')}
        />
      </FormField>
      {values.typeId === CASHBACK_TYPE && (
        <TextField
          label="Значение кэшбэка"
          type="number"
          placeholder="Значение кэшбэка"
          value={values.value}
          errors={errors.value}
          onChange={(v) => setField('value', v)}
        />
      )}
      <TextField
        label="Специальный текст *"
        placeholder="Введите текст"
        value={values.label}
        errors={errors.label}
        onChange={(v) => setField('label', v)}
      />
      <TextField
        label="Текст второго абзаца *"
        placeholder="Введите текст"
        multiline
        rows={3}
        value={values.shortDescription}
        errors={errors.short_description}
        onChange={(v) => setField('shortDescription', v, 'short_description')}
      />
      <FormField label="Уровень привилегии *" errors={errors.level_ids}>
        <MultiSelect
          options={levels.data ?? []}
          value={values.levelIds}
          isLoading={levels.isLoading}
          width={SELECT_WIDTH}
          onChange={(v) => setField('levelIds', v, 'level_ids')}
        />
      </FormField>
      <FormField label="Форма обратной связи">
        <div className="checkbox">
          <label>
            <input
              type="checkbox"
              checked={values.formActive}
              onChange={(e) => {
                setField('formActive', e.target.checked, 'form_active');
                setField('formTitle', '', 'form_title');
              }}
            />
            Показывать форму
          </label>
        </div>
      </FormField>
      {values.formActive && (
        <TextField
          label="Заголовок формы"
          placeholder="Введите текст"
          value={values.formTitle}
          errors={errors.form_title}
          onChange={(v) => setField('formTitle', v, 'form_title')}
        />
      )}
      <FileUploadField
        label="Фото (превью)"
        files={values.image}
        errors={errors.image_id}
        upload={uploadPrivilegeImage}
        remove={deletePrivilegeImage}
        onChange={(files) => setField('image', files, 'image_id')}
      />
      <SaveBar isSaving={form.isSaving} saved={form.saved} errors={errors} onSave={form.save} />
    </>
  );
};

export const PrivilegeCreatePage = () => (
  <DetailLayout title="Создание привилегии">
    <PrivilegeForm initial={EMPTY_PRIVILEGE} />
  </DetailLayout>
);

export const PrivilegeEditPage = () => {
  const id = useRouteId();
  const { data, isLoading, error } = useQuery({
    queryKey: ['loyalty-privilege', id],
    queryFn: () => fetchPrivilege(id),
  });

  return (
    <DetailLayout title="Редактирование привилегии" isLoading={isLoading} error={error}>
      {data && <PrivilegeForm key={id} id={id} initial={privilegeToValues(data)} />}
    </DetailLayout>
  );
};
